import React from 'react';

import { IS_LOCAL, isEmpty } from '@nfctron/nexus-core';
import { useContextRequired, useIsMounted } from '@nfctron/nexus-react';

import { useRouter } from 'next/router';

import { IS_DEPLOYED } from '@root/config/src/lib/constants/common';
import { useCookieConsent } from '@root/config/src/lib/modules/cookies/cookies.module';
import { CookieConsentProvider, getGtmConsentParams } from '@root/config/src/lib/modules/cookies/CookiesConsentProvider';
import { type AppTrackingModuleRegisterConfigs } from '@root/config/src/lib/modules/next/next.module';
import { FbPixelAdapter } from '@root/config/src/lib/modules/tracking/fb-pixel.adapter';
import { GoogleTagManagerAdapter } from '@root/config/src/lib/modules/tracking/google-tag-manager.adapter';
import { GtagJsAdapter } from '@root/config/src/lib/modules/tracking/gtag-js.adapter';
import { MsClarityAdapter } from '@root/config/src/lib/modules/tracking/ms-clarity.adapter';
import { SklikAdapter } from '@root/config/src/lib/modules/tracking/sklik.adapter';
import { TikTokPixelAdapter } from '@root/config/src/lib/modules/tracking/tiktok-pixel.adapter';

import { AnalyticsModule } from './analytics.module';
import { TrackingModule } from './tracking.module';

/**
 * Tracking module context
 * @private
 * @type {React.Context<TrackingModuleContextProps | undefined>}
 */
const TrackingModuleContext = React.createContext<TrackingModuleContextProps | undefined>(undefined);

/**
 * Tracking module context props
 * @private
 */
type TrackingModuleContextProps = {
	trackingModule: TrackingModule;
	analyticsModule: AnalyticsModule;
	cookieConsentCtrl: ReturnType<typeof useCookieConsent>;
};

const allowTracking = IS_DEPLOYED || IS_LOCAL;
const GA_TRACKING_ID = process.env.NEXT_PUBLIC_GA_TRACKING_ID;
const GTM_CONTAINER_ID = process.env.NEXT_PUBLIC_GTM_CONTAINER_ID;
const FB_PIXEL_TRACKING_ID = process.env.NEXT_PUBLIC_FB_PIXEL_TRACKING_ID;
const TTQ_PIXEL_TRACKING_ID = process.env.NEXT_PUBLIC_TTQ_PIXEL_TRACKING_ID;
const MS_CLARITY_TRACKING_ID = process.env.NEXT_PUBLIC_MS_CLARITY_ID;
const SKLIK_RETARGETING_ID = process.env.NEXT_PUBLIC_SKLIK_RETARGETING_ID;

/**
 * Tracking module provider
 * @type {React.Provider<TrackingModule | undefined>}
 * @export
 */
export const TrackingModuleProvider: React.FunctionComponent<TrackingModuleProviderProps> = ({ children, registerConfigs }) => {
	const router = useRouter();
	const cookieConsentCtrl = useCookieConsent(undefined);

	/* -------------------------------------------------------------------------------------------------
	 * Tracking Module
	 * -----------------------------------------------------------------------------------------------*/
	/* eslint-disable-next-line @eslint-react/naming-convention/use-state */
	const [trackingModule] = React.useState(() => {
		const fbPixelAdapter = new FbPixelAdapter(allowTracking);
		const gtagJsAdapter = new GtagJsAdapter(allowTracking);
		const googleTagManagerAdapter = new GoogleTagManagerAdapter(allowTracking);
		const tiktokPixelAdapter = new TikTokPixelAdapter(allowTracking);
		const sklikAdapter = new SklikAdapter(allowTracking);
		return new TrackingModule(fbPixelAdapter, gtagJsAdapter, googleTagManagerAdapter, tiktokPixelAdapter, sklikAdapter);
	});

	const initGtm = React.useCallback(
		(it: GoogleTagManagerAdapter) => {
			it.updateConsent(
				/** @ts-expect-error Not typed well, see https://developers.google.com/tag-platform/gtagjs/reference#consent or https://support.google.com/tagmanager/answer/10718549?hl=en */
				getGtmConsentParams(cookieConsentCtrl.consent.marketing || cookieConsentCtrl.consent.statistics || false),
			);
			it.handlePageView(window.location.href);
		},
		[cookieConsentCtrl.consent.marketing, cookieConsentCtrl.consent.statistics],
	);

	const initGtagJs = React.useCallback(
		(it: GtagJsAdapter) => {
			it.updateConsent(
				'default',
				/** @ts-expect-error Not typed well, see https://developers.google.com/tag-platform/gtagjs/reference#consent or https://support.google.com/tagmanager/answer/10718549?hl=en */
				getGtmConsentParams(cookieConsentCtrl.consent.marketing || cookieConsentCtrl.consent.statistics || false),
			);
			it.handlePageView(window.location.href);
		},
		[cookieConsentCtrl.consent.marketing, cookieConsentCtrl.consent.statistics],
	);

	const initFbPixel = React.useCallback(
		(it: FbPixelAdapter) => {
			it.updateConsent(cookieConsentCtrl.consent.marketing || false);
			it.handlePageView();
		},
		[cookieConsentCtrl.consent.marketing],
	);

	const initTiktok = React.useCallback(
		(it: TikTokPixelAdapter) => {
			it.updateConsent(cookieConsentCtrl.consent.marketing || false);
			it.handlePageView();
		},
		[cookieConsentCtrl.consent.marketing],
	);

	const initSklik = React.useCallback(
		(it: SklikAdapter) => {
			it.updateConsent(cookieConsentCtrl.consent.marketing || cookieConsentCtrl.consent.statistics || false);
			it.handlePageView({ rtgUrl: window.location.href });
		},
		[cookieConsentCtrl.consent.marketing, cookieConsentCtrl.consent.statistics],
	);

	/** update GTM & Gtag.js tracking id upon receiving a new one */
	React.useEffect(() => {
		if (registerConfigs.googleTagManager.length > 0)
			registerConfigs.googleTagManager.forEach((conf) => trackingModule.googleTagManagerAdapter.register(conf, initGtm));
		if (registerConfigs.gtagJs.length > 0) registerConfigs.gtagJs.forEach((conf) => trackingModule.gtagJsAdapter.register(conf, initGtagJs));
		/** TODO: implement un-registering after the user redirects from event-scoped page */
	}, [
		cookieConsentCtrl.consent.marketing,
		cookieConsentCtrl.consent.statistics,
		initGtagJs,
		initGtm,
		registerConfigs.googleTagManager,
		registerConfigs.gtagJs,
		trackingModule.googleTagManagerAdapter,
		trackingModule.googleTagManagerAdapter.register,
		trackingModule.gtagJsAdapter,
	]);

	/** update Facebook Pixel tracking id upon receiving a new one */
	React.useEffect(() => {
		if (registerConfigs.fbPixel.length > 0) registerConfigs.fbPixel.forEach((conf) => trackingModule.fbPixelAdapter.register(conf, initFbPixel));
		/** TODO: implement un-registering after the user redirects from event-scoped page */
	}, [cookieConsentCtrl.consent.marketing, initFbPixel, registerConfigs.fbPixel, trackingModule.fbPixelAdapter]);

	/** update TikTok Pixel tracking id upon receiving a new one */
	React.useEffect(() => {
		if (registerConfigs.ttqPixel.length > 0) registerConfigs.ttqPixel.forEach((conf) => trackingModule.tiktokPixelAdapter.register(conf, initTiktok));
		/** TODO: implement un-registering after the user redirects from event-scoped page */
	}, [cookieConsentCtrl.consent.marketing, initTiktok, registerConfigs.ttqPixel, trackingModule.tiktokPixelAdapter]);

	/** update Sklik tracking id upon receiving a new one */
	React.useEffect(() => {
		if (registerConfigs.sklik.length > 0) registerConfigs.sklik.forEach((conf) => trackingModule.sklikAdapter.register(conf, initSklik));
		/** TODO: implement un-registering after the user redirects from event-scoped page */
	}, [cookieConsentCtrl.consent.marketing, cookieConsentCtrl.consent.statistics, initSklik, registerConfigs.sklik, trackingModule.sklikAdapter]);

	/** initially register internal static tracking id */
	React.useEffect(() => {
		if (!isEmpty(GA_TRACKING_ID)) trackingModule.gtagJsAdapter.register({ trackingId: GA_TRACKING_ID }, initGtagJs);
		if (!isEmpty(GTM_CONTAINER_ID)) trackingModule.googleTagManagerAdapter.register({ trackingId: GTM_CONTAINER_ID }, initGtm);
		if (!isEmpty(FB_PIXEL_TRACKING_ID)) trackingModule.fbPixelAdapter.register({ trackingId: FB_PIXEL_TRACKING_ID }, initFbPixel);
		if (!isEmpty(TTQ_PIXEL_TRACKING_ID)) trackingModule.tiktokPixelAdapter.register({ pixelId: TTQ_PIXEL_TRACKING_ID }, initTiktok);
		if (!isEmpty(SKLIK_RETARGETING_ID)) trackingModule.sklikAdapter.register({ retargetingId: SKLIK_RETARGETING_ID }, initSklik);
	}, [
		trackingModule.fbPixelAdapter,
		trackingModule.googleTagManagerAdapter,
		trackingModule.gtagJsAdapter,
		trackingModule.tiktokPixelAdapter,
		trackingModule.sklikAdapter,
		initGtagJs,
		initGtm,
		initFbPixel,
		initTiktok,
		initSklik,
	]);

	/* -------------------------------------------------------------------------------------------------
	 * Analytics module
	 * -----------------------------------------------------------------------------------------------*/
	/* eslint-disable-next-line @eslint-react/naming-convention/use-state */
	const [analyticsModule] = React.useState(() => {
		const msClarityAdapter = new MsClarityAdapter(allowTracking);
		return new AnalyticsModule(msClarityAdapter);
	});

	const initMsClarity = React.useCallback(
		(it: MsClarityAdapter) => {
			it.updateConsent(cookieConsentCtrl.consent.statistics || false);
		},
		[cookieConsentCtrl.consent.statistics],
	);

	/** initially register internal static tracking id */
	React.useEffect(() => {
		if (!isEmpty(MS_CLARITY_TRACKING_ID)) analyticsModule.msClarityAdapter.register({ trackingId: MS_CLARITY_TRACKING_ID }, initMsClarity);
	}, [analyticsModule.msClarityAdapter, initMsClarity]);

	/** update MsClarity tracking id upon receiving a new one */
	React.useEffect(() => {
		if (registerConfigs.msClarity.length > 0) registerConfigs.msClarity.forEach((id) => analyticsModule.msClarityAdapter.register(id, initMsClarity));
	}, [registerConfigs.msClarity, analyticsModule.msClarityAdapter, initMsClarity]);

	/** register route change events */
	React.useEffect(() => {
		router.events.on('routeChangeComplete', trackingModule.handlePageViewChange);
		return () => {
			router.events.off('routeChangeComplete', trackingModule.handlePageViewChange);
		};
	}, [trackingModule.handlePageViewChange, router.events]);

	const trackingModuleCtx = React.useMemo(
		() => ({
			trackingModule,
			analyticsModule,
			cookieConsentCtrl,
		}),
		[trackingModule, analyticsModule, cookieConsentCtrl],
	);

	return (
		<React.Fragment>
			<TrackingModuleContext value={trackingModuleCtx}>
				<CookieConsentProvider>{children}</CookieConsentProvider>
			</TrackingModuleContext>
		</React.Fragment>
	);
};

/**
 * Tracking module provider props
 * @private
 */
interface TrackingModuleProviderProps {
	children: React.ReactNode;
	registerConfigs: AppTrackingModuleRegisterConfigs;
}

/**
 * Hook to access tracking module context
 */
export const useTracking = () => useContextRequired(TrackingModuleContext);
